import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import AskAnythingContent from '@/components/askAnything/ask/AskAnythingContent'
import NewChatCompose from '@/components/askAnything/ask/NewChatCompose'
import WaveBackground from '@/components/animations/WaveBackground'
import ThreadsMenu from '@/components/chat/ThreadsMenu'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useChatStore } from './chatStore'
import { useTheme } from '@/context/ThemeContext'
import { nanoid } from '@/lib/nanoid'
import { AuthContext } from '@/context/AuthContext'

const SkeletonLoader = () => {
  const { isDarkMode } = useTheme()

  return (
    <div className={`absolute inset-0 flex ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="w-[320px] h-full border-r">
        <div className="p-4 border-b">
          <Skeleton className="h-8 w-full mb-4" />
          <div className="space-y-3 mt-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-4/5" />
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="h-topbar border-b flex items-center px-6">
          <Skeleton className="h-8 w-40" />
          <div className="ml-auto flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        </div>
        <div className="flex-1 p-6 space-y-6 overflow-hidden">
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-20 w-[400px]" />
            </div>
          </div>
          <div className="flex items-start gap-3 justify-end">
            <div className="space-y-2">
              <Skeleton className="h-4 w-40 ml-auto" />
              <Skeleton className="h-12 w-[300px]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function AskAnything() {
  const { threadId: threadIdParam } = useParams()
  const navigate = useNavigate()

  function handleNewChatClick() {
    navigate('/ask-anything')
  }

  function switchThread(threadId: string) {
    navigate(`/ask-anything/${threadId}`)
  }

  return (
    <AskAnythingInternal
      threadIdParam={threadIdParam}
      onNewChatClick={handleNewChatClick}
      switchThread={switchThread}
    />
  )
}

export function AskAnythingInternal({
  threadIdParam,
  onNewChatClick,
  switchThread,
}: {
  threadIdParam?: string
  onNewChatClick: () => void
  switchThread: (threadId: string) => void
}) {
  const { selectedProduct, initData, error, threadId, setThreadId, setIsEmptyThread, isEmptyThread } = useChatStore()
  const { isLoading: isAuthLoading, isAuthenticated } = React.useContext(AuthContext)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [localError, setLocalError] = useState<string | null>(null)
  const { isDarkMode } = useTheme()

  useLayoutEffect(() => {
    console.log('-- threadIdParam', threadIdParam)
    if (threadIdParam) {
      setThreadId(threadIdParam)
      setIsEmptyThread(false)
    } else {
      setIsEmptyThread(true)
      setThreadId(nanoid())
    }
  }, [threadIdParam])

  useEffect(() => {
    // Wait until auth state is resolved before initializing data to avoid premature 401s
    if (isAuthLoading || !isAuthenticated) return

    const init = async () => {
      try {
        await initData(threadId)
      } catch (error) {
        console.error('Error initializing data:', error)
        setLocalError('Failed to load chat data. Please try again later.')
      } finally {
        setIsInitialLoading(false)
      }
    }
    init()
  }, [threadId, isAuthLoading, isAuthenticated])

  // Handle loading state (also show while auth is loading)
  const isComposeMode = isEmptyThread || !selectedProduct
  if (!isComposeMode && (isInitialLoading || isAuthLoading)) {
    return <SkeletonLoader />
  }

  // Handle error state
  if (error || localError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-5 w-5" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error || localError}
            <div className="mt-4">
              <Button variant="outline" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Show new chat compose mode when no threadId is present
  if (isComposeMode) {
    return (
      <div className={`flex h-full min-h-0 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
        <ThreadsMenu currentThreadId="" onNewChatClick={onNewChatClick} switchThread={switchThread} />
        <div className="flex-1 flex flex-col min-h-0 relative">
          <div className="absolute inset-0 z-0">
            <WaveBackground isLoading={false}>
              <NewChatCompose />
            </WaveBackground>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex h-full min-h-0 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
      <ThreadsMenu currentThreadId={threadId} onNewChatClick={onNewChatClick} switchThread={switchThread} />

      <div className="flex-1 flex flex-col min-h-0 relative">
        <div className="absolute inset-0 z-0">
          <AskAnythingContent currentThreadId={threadId} />
        </div>
      </div>
    </div>
  )
}
